import React from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@hyro-components/Tooltip";
import palette from "src/theme/palette";
import dayjs from "dayjs";
import { GO_LIVE_SET_UP_LINK, buildSalesforceAssetsUrl } from "./SubscriptionTab.consts";

const GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(1, minmax(0, 1fr))",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(5, minmax(0, 1fr))"
  },
  columnGap: 3,
  rowGap: 3,
  padding: "8px 18px"
};

const DetailColumn = ({ label, value, labelTooltip, valueColor }) => (
  <Stack gap="2px">
    <Stack direction="row" alignItems="center" gap="8px">
      <Typography
        variant="body2"
        sx={{ color: palette.text[700], lineHeight: "20px", letterSpacing: "0.15px" }}
      >
        {label}
      </Typography>
      {labelTooltip && (
        <Tooltip title={labelTooltip} placement="top" variant="light">
          <InfoOutlinedIcon sx={{ fontSize: 14, color: palette.text[700] }} />
        </Tooltip>
      )}
    </Stack>
    <Typography
      variant="subtitle2"
      sx={{
        color: valueColor ?? palette.text[800],
        lineHeight: "22px",
        letterSpacing: "0.1px"
      }}
    >
      {value == null || value === "" ? "—" : value}
    </Typography>
  </Stack>
);

const formatDate = (value) => (value ? dayjs(value).format("MMM D, YYYY") : null);

const ARR_FORMAT_OPTIONS = {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0
};

const formatArr = (value) => (value == null ? null : value.toLocaleString("en-US", ARR_FORMAT_OPTIONS));

const formatAutoRenew = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return null;
};

const formatFirstYearTermination = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return null;
};

const isRenewalDatePast = (renewalDate) =>
  !!renewalDate && dayjs(renewalDate).isBefore(dayjs(), "day");

const renderAgreement = (url) =>
  url ? (
    <Link href={url} target="_blank" rel="noopener noreferrer" underline="always">
      View
    </Link>
  ) : null;

const renderGoLive = (goLiveDate, salesforceAccountId) => {
  if (goLiveDate) return formatDate(goLiveDate);
  if (!salesforceAccountId) return null;
  return (
    <Link
      href={buildSalesforceAssetsUrl(salesforceAccountId)}
      target="_blank"
      rel="noopener noreferrer"
      underline="always"
      data-testid="go-live-set-up-link"
      sx={{ color: palette.text[700] }}
    >
      {GO_LIVE_SET_UP_LINK}
    </Link>
  );
};

const SubscriptionDetails = ({ term, salesforceAccountId }) => (
  <Box sx={GRID_SX} data-testid="subscription-details-card">
    <DetailColumn label="Contract signage" value={formatDate(term.contractSignageDate)} />
    <DetailColumn label="Go live" value={renderGoLive(term.goLiveDate, salesforceAccountId)} />
    <DetailColumn
      label="Term start date"
      value={formatDate(term.termStartDate)}
      labelTooltip="The term used to calculate usage out of contracted quota (Annually)"
    />
    <DetailColumn label="Term end date" value={formatDate(term.termEndDate)} />
    <DetailColumn label="Auto renew" value={formatAutoRenew(term.autoRenew)} />
    <DetailColumn label="ARR" value={formatArr(term.arr)} />
    {term.firstYearTerminationAllowed != null && (
      <DetailColumn
        label="First year termination"
        value={formatFirstYearTermination(term.firstYearTerminationAllowed)}
      />
    )}
    <DetailColumn label="Agreement" value={renderAgreement(term.agreementUrl)} />
    <DetailColumn
      label="Renewal date"
      value={formatDate(term.renewalDate)}
      valueColor={isRenewalDatePast(term.renewalDate) ? palette.error[500] : undefined}
    />
  </Box>
);

export default SubscriptionDetails;
